import { useState } from "react";
import * as yup from "yup";

import { generateValidationSchema } from "@/domains/datablocks/fields/validators";
import i18n from "@/libraries/i18n";
import { DataTemplateResponse } from "@/redux/datablocks/types";
import { DataBlockFormFields } from "@/domains/datablocks/types";

const { t } = i18n;

export const createDataBlockSchema = () => {
  return yup
    .object()
    .shape({
      dataTemplate: yup
        .number()
        .typeError(t("datablocks.errors.dataTemplateNotValid"))
        .required(t("datablocks.errors.dataTemplateRequired")),
      organization: yup
        .number()
        .typeError(t("datablocks.errors.required"))
        .required(t("datablocks.errors.required")),
      sharingRights: yup
        .string()
        .typeError(t("datablocks.errors.required"))
        .required(t("datablocks.errors.required")),
      request: yup.number().typeError(t("datablocks.errors.required")),
      fieldValues: yup
        .array()
        .of(
          yup.object().shape({
            field: yup.number(),
            value: yup.mixed(),
          }),
        )
        .optional(),
    })
    .required();
};

export const useDataBlockValidation = () => {
  const [validationSchema, setValidationSchema] = useState<
    yup.ObjectSchema<DataBlockFormFields>
  >(
    // @ts-expect-error schema
    createDataBlockSchema(),
  );

  const updateSchemaForTemplate = (template: DataTemplateResponse) => {
    // Filter out disabled fields - they should not be validated
    const enabledFields = template.templateFields.filter(
      (field) => !field.disabled,
    );

    // Get set of disabled field IDs for filtering validation rules
    const disabledFieldIds = new Set(
      template.templateFields
        .filter((field) => field.disabled)
        .map((field) => field.id),
    );

    // Filter validation rules to exclude disabled fields
    const filteredValidationRules = template.validationRules
      .map((rule) => ({
        ...rule,
        ruleFields: rule.ruleFields?.filter(
          (fieldId) => !disabledFieldIds.has(fieldId),
        ),
      }))
      .filter((rule) => {
        // Remove rules that have no valid fields left
        if (rule.ruleFields && rule.ruleFields.length === 0) {
          return false;
        }
        return true;
      });

    const templateSchema = generateValidationSchema(
      enabledFields,
      filteredValidationRules,
      t,
    );
    const newSchema = createDataBlockSchema().concat(templateSchema);
    setValidationSchema(newSchema);
    return newSchema;
  };

  return {
    validationSchema,
    updateSchemaForTemplate,
  };
};
